import React, { useState } from "react";
import {
  Ship,
  Plane,
  Truck,
  Package,
  Users,
  CreditCard,
  FileText,
  Shield,
  ChevronDown,
  ChevronUp,
  DollarSign,
  Clock,
  AlertTriangle,
  CheckCircle2,
  Info,
  Lock,
  Settings,
  Save,
  XCircle,
} from "lucide-react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import CelebrationButton from "../ui/CelebrationButton/CelebrationButton";
import moment from "moment";

// Mode configuration tab component for Address Book
const ModeConfigTab = ({
  modeId,
  modeLabel,
  ModeIcon,
  modeColor,
  modeData,
  onModeDataChange,
  isAnimate,
  onSave,
}) => {
  const [expandedSections, setExpandedSections] = useState({
    creditTerms: true,
    sopRules: true,
    permissions: true,
  });

  const paymentTermsOptions = [
    { id: 1, label: "Prepaid" },
    { id: 2, label: "COD" },
    { id: 3, label: "Net 15" },
    { id: 4, label: "Net 30" },
    { id: 5, label: "Net 45" },
    { id: 6, label: "Net 60" },
    { id: 7, label: "Net 90" },
  ];

  const bookingApprovalOptions = [
    { id: 1, label: "Manager Approval" },
    { id: 2, label: "Director Approval" },
    { id: 3, label: "Credit Team Approval" },
  ];
  const creditOverrideOptions = [
    { id: 1, label: "Manager Only" },
    { id: 2, label: "Director Only" },
    { id: 3, label: "CFO Only" },
  ];

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleChange = (field, value, label) => {
    onModeDataChange(modeId, field, value, label);
  };

  const SectionHeader = ({
    icon: Icon,
    title,
    section,
    badge,
    color = "bg-slate-600",
  }) => (
    <button
      type="button"
      onClick={() => toggleSection(section)}
      className="w-full flex items-center justify-between px-4 py-2.5 bg-gradient-to-r from-slate-50 to-white dark:from-slate-800/80 dark:to-slate-900 border-b border-slate-200 dark:border-slate-700 hover:from-slate-100 dark:hover:from-slate-700 transition-all"
    >
      <div className="flex items-center gap-2">
        <div className={`p-1.5 rounded-md ${color}`}>
          <Icon className="w-3.5 h-3.5 text-white" />
        </div>
        <h2 className="text-sm font-semibold text-slate-800 dark:text-white">
          {title}
        </h2>
        {badge && (
          <span className="px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-[10px] font-medium rounded-full">
            {badge}
          </span>
        )}
      </div>
      {expandedSections[section] ? (
        <ChevronUp className="w-4 h-4 text-slate-400" />
      ) : (
        <ChevronDown className="w-4 h-4 text-slate-400" />
      )}
    </button>
  );

  const inputClass =
    "w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all";

  return (
    <div className="space-y-4">
      {/* Mode Header */}
      <div className={`bg-gradient-to-r ${modeColor} rounded-lg p-4 shadow-sm`}>
        <div className="flex items-center gap-3">
          <div className="bg-white/20 p-2 rounded-lg">
            <ModeIcon className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white">
              {modeLabel} Configuration
            </h3>
            <p className="text-sm text-white/80">
              Configure credit terms, SOPs, and permissions for{" "}
              {modeLabel.toLowerCase()} operations
            </p>
          </div>
        </div>
      </div>

      {/* Credit Terms Section */}
      <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
        <SectionHeader
          icon={CreditCard}
          title="Credit Terms & Limits"
          section="creditTerms"
          color="bg-emerald-600"
        />
        {expandedSections.creditTerms && (
          <div className="p-4 space-y-4">
            {/* Credit Limit */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                  Credit Limit ($)
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={modeData?.creditLimit || ""}
                    onChange={(e) =>
                      handleChange("creditLimit", e.target.value)
                    }
                    className={`${inputClass} pl-9`}
                    data-testid={`${modeId}-credit-limit`}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                  Payment Terms (Days)
                </label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <select
                    value={modeData?.paymentTermsId || ""}
                    onChange={(e) => {
                      const selectedId = e.target.value;

                      const selectedOption = paymentTermsOptions.find(
                        (item) => item.id == selectedId,
                      );
                      console.log("selectedOption", selectedOption);
                      handleChange(
                        "paymentTermsId",
                        selectedId,
                        selectedOption?.label,
                      ); // "net30"
                    }}
                    className={`${inputClass} pl-9`}
                    data-testid={`${modeId}-payment-terms`}
                  >
                    <option value="">Select</option>

                    {paymentTermsOptions.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                  Credit Status
                </label>
                <select
                  value={modeData?.creditStatus || ""}
                  onChange={(e) => handleChange("creditStatus", e.target.value)}
                  className={inputClass}
                  data-testid={`${modeId}-credit-status`}
                >
                  <option value="">Select</option>
                  <option value="approved">Approved</option>
                  <option value="pending">Pending Review</option>
                  <option value="hold">On Hold</option>
                  <option value="prepay_only">Prepay Only</option>
                </select>
              </div>
            </div>

            {/* Credit Warning Thresholds */}
            <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-amber-800 dark:text-amber-300">
                    Credit Alert Thresholds
                  </h4>
                  <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
                    Define warning levels for credit utilization
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                <div>
                  <label className="block text-xs font-medium text-amber-800 dark:text-amber-300 mb-1">
                    Warning Level (%)
                  </label>
                  <Input
                    type="number"
                    placeholder="70"
                    min="0"
                    max="100"
                    value={modeData?.creditAlertThresholdsWarningLevel || ""}
                    onChange={(e) =>
                      handleChange(
                        "creditAlertThresholdsWarningLevel",
                        e.target.value,
                      )
                    }
                    className={inputClass}
                    data-testid={`${modeId}-warning-threshold`}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-amber-800 dark:text-amber-300 mb-1">
                    Critical Level (%)
                  </label>
                  <Input
                    type="number"
                    placeholder="90"
                    min="0"
                    max="100"
                    value={modeData?.creditAlertThresholdsCriticalLevel || ""}
                    onChange={(e) =>
                      handleChange(
                        "creditAlertThresholdsCriticalLevel",
                        e.target.value,
                      )
                    }
                    className={inputClass}
                    data-testid={`${modeId}-critical-threshold`}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-amber-800 dark:text-amber-300 mb-1">
                    Auto-Block at (%)
                  </label>
                  <Input
                    type="number"
                    placeholder="100"
                    min="0"
                    max="150"
                    value={modeData?.creditAlertThresholdsAutoBlockAt || ""}
                    onChange={(e) =>
                      handleChange(
                        "creditAlertThresholdsAutoBlockAt",
                        e.target.value,
                      )
                    }
                    className={inputClass}
                    data-testid={`${modeId}-block-threshold`}
                  />
                </div>
              </div>
            </div>

            {/* Override Options */}
            <div className="flex flex-wrap gap-4 mt-3">
              <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 cursor-pointer">
                <input
                  type="checkbox"
                  checked={modeData?.allowCreditLimitOverride || false}
                  onChange={(e) =>
                    handleChange("allowCreditLimitOverride", e.target.checked)
                  }
                  className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                  data-testid={`${modeId}-allow-override`}
                />
                Allow credit limit override
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 cursor-pointer">
                <input
                  type="checkbox"
                  checked={modeData?.emailNotificationOnThreshold || false}
                  onChange={(e) =>
                    handleChange(
                      "emailNotificationOnThreshold",
                      e.target.checked,
                    )
                  }
                  className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                  data-testid={`${modeId}-notify-threshold`}
                />
                Email notification on threshold
              </label>
            </div>
          </div>
        )}
      </div>

      {/* SOP & Rules Section */}
      <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
        <SectionHeader
          icon={FileText}
          title="Standard Operating Procedures (SOP)"
          section="sopRules"
          color="bg-blue-600"
          badge="Configure"
        />
        {expandedSections.sopRules && (
          <div className="p-4 space-y-4">
            {/* Booking Rules */}
            <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700">
              <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
                <Settings className="w-4 h-4" /> Booking Rules
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={
                      modeData?.sopBookingRulesRequirePoNumberForBooking ||
                      false
                    }
                    onChange={(e) =>
                      handleChange(
                        "sopBookingRulesRequirePoNumberForBooking",
                        e.target.checked,
                      )
                    }
                    className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                    data-testid={`${modeId}-require-po`}
                  />
                  Require PO Number for booking
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={
                      modeData?.sopBookingRulesRequireDocumentsBeforeRelease ||
                      false
                    }
                    onChange={(e) =>
                      handleChange(
                        "sopBookingRulesRequireDocumentsBeforeRelease",
                        e.target.checked,
                      )
                    }
                    className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                    data-testid={`${modeId}-require-documents`}
                  />
                  Require documents before release
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={
                      modeData?.sopBookingRulesAutoSendBookingConfirmation ||
                      false
                    }
                    onChange={(e) =>
                      handleChange(
                        "sopBookingRulesAutoSendBookingConfirmation",
                        e.target.checked,
                      )
                    }
                    className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                    data-testid={`${modeId}-auto-send-confirm`}
                  />
                  Auto-send booking confirmation
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={
                      modeData?.sopBookingRulesRequireRateApprovalBeforeBooking ||
                      false
                    }
                    onChange={(e) =>
                      handleChange(
                        "sopBookingRulesRequireRateApprovalBeforeBooking",
                        e.target.checked,
                      )
                    }
                    className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                    data-testid={`${modeId}-require-rate-approval`}
                  />
                  Require rate approval before booking
                </label>
              </div>
            </div>

            {/* Document Rules */}
            <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700">
              <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4" /> Document Rules
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={
                      modeData?.sopDocumentRulesAutoGenerateHblHawb || false
                    }
                    onChange={(e) =>
                      handleChange(
                        "sopDocumentRulesAutoGenerateHblHawb",
                        e.target.checked,
                      )
                    }
                    className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                    data-testid={`${modeId}-auto-generate-hbl`}
                  />
                  Auto-generate HBL/HAWB
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={
                      modeData?.sopDocumentRulesRequireCustomsClearanceDocs ||
                      false
                    }
                    onChange={(e) =>
                      handleChange(
                        "sopDocumentRulesRequireCustomsClearanceDocs",
                        e.target.checked,
                      )
                    }
                    className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                    data-testid={`${modeId}-require-customs`}
                  />
                  Require customs clearance docs
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={
                      modeData?.sopDocumentRulesSendArrivalNotice || false
                    }
                    onChange={(e) =>
                      handleChange(
                        "sopDocumentRulesSendArrivalNotice",
                        e.target.checked,
                      )
                    }
                    className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                    data-testid={`${modeId}-send-arrival`}
                  />
                  Send arrival notice
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={
                      modeData?.sopDocumentRulesAutoSendDeliveryOrder || false
                    }
                    onChange={(e) =>
                      handleChange(
                        "sopDocumentRulesAutoSendDeliveryOrder",
                        e.target.checked,
                      )
                    }
                    className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                    data-testid={`${modeId}-send-delivery`}
                  />
                  Auto-send delivery order
                </label>
              </div>
            </div>

            {/* Special Instructions */}
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                Special Instructions / Notes
              </label>
              <Textarea
                placeholder={`Enter special instructions for ${modeLabel.toLowerCase()} shipments...`}
                value={modeData?.sopSpecialInstructionsOrNotes || ""}
                onChange={(e) =>
                  handleChange("sopSpecialInstructionsOrNotes", e.target.value)
                }
                className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 min-h-[80px]"
                data-testid={`${modeId}-special-instructions`}
              />
            </div>
          </div>
        )}
      </div>

      {/* Permissions & Governance Section */}
      <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
        <SectionHeader
          icon={Shield}
          title="Permissions & Governance"
          section="permissions"
          color="bg-purple-600"
        />
        {expandedSections.permissions && (
          <div className="p-4 space-y-4">
            {/* Access Controls */}
            <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
              <div className="flex items-start gap-2 mb-3">
                <Lock className="w-4 h-4 text-purple-600 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-purple-800 dark:text-purple-300">
                    Access Controls
                  </h4>
                  <p className="text-xs text-purple-700 dark:text-purple-400 mt-0.5">
                    Define who can perform actions for this mode
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-purple-800 dark:text-purple-300 mb-1">
                    Booking Approval Required
                  </label>
                  <select
                    value={
                      modeData?.accessControlsBookingApprovalRequiredId || ""
                    }
                    onChange={(e) => {
                      const selectedId = e.target.value;

                      const selectedOption = bookingApprovalOptions.find(
                        (item) => item.id == selectedId,
                      );

                      handleChange(
                        "accessControlsBookingApprovalRequiredId",
                        selectedId,
                        selectedOption?.label,
                      );
                    }}
                    className={inputClass}
                    data-testid={`${modeId}-booking-approval`}
                  >
                    <option value="" disabled>
                      None Required
                    </option>

                    {bookingApprovalOptions.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-purple-800 dark:text-purple-300 mb-1">
                    Credit Override Approval
                  </label>
                  <select
                    value={
                      modeData?.accessControlsCreditOverrideApprovalId || ""
                    }
                    onChange={(e) => {
                      const selectedId = e.target.value;

                      const selectedOption = creditOverrideOptions.find(
                        (item) => item.id == selectedId,
                      );

                      handleChange(
                        "accessControlsCreditOverrideApprovalId",
                        selectedId,
                        selectedOption?.label,
                      );
                    }}
                    className={inputClass}
                    data-testid={`${modeId}-credit-override-level`}
                  >
                    <option value="" disabled>
                      None Required
                    </option>

                    {creditOverrideOptions.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Status Display */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-2">
                  {modeData?.isActive ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-500" />
                  )}
                  <span className="text-xs font-medium text-slate-600 dark:text-slate-400">
                    Mode Status
                  </span>
                </div>
                <p
                  className={`text-sm font-semibold mt-1 ${
                    modeData?.isActive
                      ? "text-emerald-600 dark:text-emerald-400"
                      : "text-red-600 dark:text-red-400"
                  }`}
                >
                  {modeData?.isActive ? "Active" : "Inactive"}
                </p>
              </div>
              <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-2">
                  <Info className="w-4 h-4 text-blue-500" />
                  <span className="text-xs font-medium text-slate-600 dark:text-slate-400">
                  {modeData?.updatedAt?"Last Updated":"Created At"}
                  </span>
                </div>
                <p className="text-sm font-semibold text-slate-700 dark:text-slate-300 mt-1">
                  {modeData?.updatedAt? moment(modeData?.updatedAt).format("DD MMM YYYY"):"" || moment(modeData?.createdAt).format("DD MMM YYYY") || "Not yet configured"}
                </p>
              </div>
              <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-violet-500" />
                  <span className="text-xs font-medium text-slate-600 dark:text-slate-400">
                    {modeData?.updatedBy ? "Updated By" : "Create By"}
                  </span>
                </div>
                <p className="text-sm font-semibold text-slate-700 dark:text-slate-300 mt-1">
                  {modeData?.updatedBy || modeData?.createdBy || "-"}
                </p>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                Governance Notes
              </label>
              <Textarea
                placeholder="Add any governance-related notes or compliance requirements..."
                value={modeData?.governanceNotes || ""}
                onChange={(e) =>
                  handleChange("governanceNotes", e.target.value)
                }
                className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 min-h-[60px]"
                data-testid={`${modeId}-governance-notes`}
              />
            </div>
          </div>
        )}
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <CelebrationButton
          onClick={onSave}
          label={`Save ${modeLabel} Configuration`}
          className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium transition-all shadow-md hover:shadow-lg flex items-center gap-2"
          icon={<Save className="w-4 h-4" />}
          isSuccess={isAnimate}
        />
      </div>
    </div>
  );
};

export default ModeConfigTab;
